import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api.ts';
import { useAuth } from '../state/auth.tsx';
import { Alert, Spinner, TextField } from '../components/ui.tsx';

const MESSAGES: Record<string, string> = {
  invalid_credentials: 'Email o password non corretti.',
  email_not_verified: 'Devi ancora confermare l’indirizzo email. Controlla la posta.',
};

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // An account that never got its confirmation email is stuck until it can ask for another one.
  const [unverified, setUnverified] = useState(false);
  const [resent, setResent] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setUnverified(false);
    setResent(false);
    setBusy(true);
    try {
      await login(email.trim(), password);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(MESSAGES[err.code] ?? 'Accesso non riuscito, riprova.');
        setUnverified(err.code === 'email_not_verified');
      } else {
        setError('Sembra che tu sia offline. Riprova quando torna la rete.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setBusy(true);
    try {
      await api.resend(email.trim());
      setResent(true);
    } catch {
      setError('Non riesco a contattare il server.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="content narrow">
      <div className="stack">
        <div className="center">
          <img src="/icons/icon-192.png" alt="" width={72} height={72} style={{ borderRadius: 18 }} />
          <h1 style={{ marginBottom: 4 }}>Klarnus</h1>
          <p className="muted">Le tue carte fedeltà, sempre in tasca.</p>
        </div>

        <form className="stack" onSubmit={submit} noValidate>
          {error && <Alert kind="error">{error}</Alert>}
          {resent && <Alert kind="info">Email inviata di nuovo. Controlla la posta, anche nello spam.</Alert>}
          {unverified && !resent && (
            <button className="btn secondary block" type="button" onClick={() => void resend()} disabled={busy}>
              Rimandami l’email di conferma
            </button>
          )}
          <TextField
            id="email" label="Email" type="email" inputMode="email" autoComplete="username"
            required value={email} onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            id="password" label="Password" type="password" autoComplete="current-password"
            required value={password} onChange={(e) => setPassword(e.target.value)}
          />
          <button className="btn block" type="submit" disabled={busy}>
            {busy ? <Spinner /> : 'Entra'}
          </button>
        </form>

        <p className="center muted">
          <Link to="/forgot">Password dimenticata?</Link>
        </p>
        <p className="center">
          Non hai un account? <Link to="/register">Registrati</Link>
        </p>
      </div>
    </div>
  );
}
